<template>
  <!--
    操作指引（新手引导）通用组件
    作用：在目标元素上方盖一层半透明遮罩，并把当前引导目标"挖洞"高亮出来，
          旁边显示一个气泡，气泡里是标题 + 说明文字 + 上一步/下一步/跳过 按钮。

    支持的两种显示模式：
      1. 普通模式（默认）：高亮某个目标元素，气泡跟随元素位置；
      2. 居中模式（step.mode === 'center'）：不高亮元素，全屏浅遮罩 + 居中气泡，
         用于"进入页面后介绍页面"的场景。

    使用方式（父组件）：
      <GuideTour
        :visible.sync="guideVisible"
        :steps="guideSteps"
        @close-menus="handleGuideCloseMenus"
        @open-menu="handleGuideOpenMenu"
        @navigate="handleGuideNavigate"
        @finish="onGuideFinish"
        @skip="onGuideFinish"
      />

    props：
      visible : Boolean  是否显示引导（用 .sync 双向绑定）
      steps   : Array    引导步骤数组，每项结构：
        {
          type: 'menu' | 'submenu' | 'page' | 'element',  // 步骤类型
          selector: String,        // 目标元素选择器（普通模式必填）
          mode: 'center',          // 可选，设为 'center' 时居中显示气泡
          title: String,           // 气泡标题
          content: String,         // 气泡说明文字
          placement: String,       // 气泡方向：top/bottom/left/right，默认 right
          openMenuIndex: String,   // submenu 步骤：定位前需展开的一级菜单 index
          route: String,           // page/element 步骤：定位前需跳转到的路由
          action: 'click',         // 可选，定位后自动点击该元素（打开弹窗/切换标签页）
          actionWait: Number,      // 可选，点击后等待时长(ms)，默认 600
        }

    events：
      update:visible  关闭引导时触发（.sync 已处理）
      close-menus     步骤为 menu 时触发，提示父组件折叠所有一级菜单
      open-menu       步骤为 submenu 时触发，参数为需展开的一级菜单 index
      navigate        步骤为 page/element 时触发，参数为需跳转的路由
      change          步骤切换时触发，参数为当前步骤下标
      finish          点击"完成"（最后一步）时触发
      skip            点击"跳过"时触发
  -->
  <div v-if="visible && currentStep" class="guide-root">
    <!-- 居中模式：全屏浅遮罩 -->
    <div v-if="isCenter" class="guide-mask guide-mask-full" />
    <!-- 普通模式：挖洞遮罩 + 高亮框 -->
    <template v-else>
      <div v-for="m in masks" :key="m.key" class="guide-mask" :style="m.style" />
      <div class="guide-highlight" :style="highlightStyle" />
    </template>

    <!-- 说明气泡 -->
    <div class="guide-popover" :class="{ 'is-center': isCenter }" :style="popoverStyle">
      <div v-if="!isCenter" class="guide-popover-arrow" :class="'arrow-' + (currentStep.placement || 'right')" />

      <!-- 级联目录导航：hover 展开子菜单，点击任意节点跳转到对应引导步骤 -->
      <el-cascader
        ref="cascader"
        v-model="currentCascaderValue"
        :options="cascaderOptions"
        :props="cascaderProps"
        :show-all-levels="false"
        :append-to-body="false"
        size="small"
        class="guide-cascader"
        popper-class="guide-cascader-popper"
      />

      <div class="guide-popover-content">{{ currentStep.content }}</div>

      <div class="guide-popover-footer">
        <el-button size="mini" @click="skip">跳过</el-button>
        <div>
          <el-button v-if="stepIndex > 0" size="mini" @click="prev">上一步</el-button>
          <el-button type="primary" size="mini" @click="next">
            {{ isLast ? '完成' : '下一步' }}
          </el-button>
        </div>
      </div>

      <!-- <div class="guide-popover-step">{{ stepIndex + 1 }} / {{ steps.length }}</div> -->
    </div>
  </div>
</template>

<script>
export default {
  name: 'GuideTour',

  props: {
    visible: { type: Boolean, default: false },
    steps: { type: Array, default: () => [] },
  },

  data() {
    return {
      // 当前步骤下标（从 0 开始）
      stepIndex: 0,
      // 目标元素在视口中的位置和尺寸
      targetRect: { top: 0, left: 0, width: 0, height: 0 },
      // 级联目录配置：hover 展开子菜单、任意层级节点都可选中跳转
      cascaderProps: {
        expandTrigger: 'hover',
        checkStrictly: true,
        value: 'value',
        label: 'label',
        children: 'children',
      },
    }
  },

  computed: {
    // 当前步骤对象
    currentStep() {
      return this.steps[this.stepIndex] || null
    },
    // 是否最后一步
    isLast() {
      return this.stepIndex === this.steps.length - 1
    },
    // 是否居中显示模式（用于"进入页面介绍页面"的场景）
    isCenter() {
      return this.currentStep && this.currentStep.mode === 'center'
    },
    // 级联目录树：把扁平 steps 按「一级菜单 → 二级菜单 → 分组 → 元素」组织成树
    // - 带 action: 'click' 的步骤作为「父级分组」，其后元素挂到它下面
    // - 「添加」按钮视为打开弹窗（顶层分组）；弹窗内的标签切换（#tab-xxx）嵌套在弹窗分组下；
    //   页面内的标签切换（#tab-xxx）则平级挂在二级菜单下
    // - 每个节点的 value 为该步骤在 steps 中的下标，点击即可跳转。
    cascaderOptions() {
      const options = []
      let menu = null
      let submenu = null
      let group = null // 当前元素挂载点：二级菜单或最近的点击分组
      let dialogOpen = false // 当前是否在弹窗上下文内（已点击「添加」）

      this.steps.forEach((step, index) => {
        const node = { value: index, label: step.title }

        if (step.type === 'menu') {
          // 一级菜单：作为根节点
          node.children = []
          options.push(node)
          menu = node
          submenu = null
          group = null
          dialogOpen = false
        } else if (step.type === 'submenu') {
          // 二级菜单：挂到当前一级菜单下
          node.children = []
          if (menu) menu.children.push(node)
          else options.push(node)
          submenu = node
          group = node // 元素默认先挂到二级菜单下
          dialogOpen = false
        } else if (step.action === 'click') {
          // 点击步骤：作为父级分组，其后元素挂到它下面
          node.children = []
          const isDialog = (step.selector || '').indexOf('添加按钮') !== -1
          if (isDialog) {
            // 打开弹窗：作为二级菜单下的顶层分组
            if (submenu) submenu.children.push(node)
            else if (menu) menu.children.push(node)
            else options.push(node)
            group = node
            dialogOpen = true
          } else if (dialogOpen) {
            // 弹窗内的标签页切换：嵌套在当前弹窗分组下
            if (group) group.children.push(node)
            else if (submenu) submenu.children.push(node)
            else options.push(node)
            group = node
          } else {
            // 页面内的标签页切换：平级挂在二级菜单下
            if (submenu) submenu.children.push(node)
            else if (menu) menu.children.push(node)
            else options.push(node)
            group = node
          }
        } else {
          // 普通页面/元素：挂到当前挂载点（二级菜单或最近的点击分组）
          if (group) group.children.push(node)
          else if (submenu) submenu.children.push(node)
          else if (menu) menu.children.push(node)
          else options.push(node)
        }
      })

      // 清理空的 children，避免 Cascader 显示无内容的展开箭头
      this.cleanEmptyChildren(options)
      return options
    },
    // 当前步骤在级联树中的完整路径（用于 Cascader 回显当前选中项）
    currentCascaderValue: {
      get() {
        return this.findPath(this.cascaderOptions, this.stepIndex) || []
      },
      set(values) {
        this.handleCascaderChange(values)
      },
    },
    // 四块遮罩，围绕目标元素四周，形成"挖洞"效果
    masks() {
      const { top, left, width, height } = this.targetRect
      const vw = window.innerWidth
      const vh = window.innerHeight
      const bottom = top + height
      const right = left + width
      return [
        { key: 'top', style: { top: '0px', left: '0px', width: '100%', height: top + 'px' } },
        { key: 'bottom', style: { top: bottom + 'px', left: '0px', width: '100%', height: (vh - bottom) + 'px' } },
        { key: 'left', style: { top: top + 'px', left: '0px', width: left + 'px', height: height + 'px' } },
        { key: 'right', style: { top: top + 'px', left: right + 'px', width: (vw - right) + 'px', height: height + 'px' } },
      ]
    },
    // 高亮框样式
    highlightStyle() {
      const { top, left, width, height } = this.targetRect
      return { top: top + 'px', left: left + 'px', width: width + 'px', height: height + 'px' }
    },
    // 气泡位置：居中模式固定居中，普通模式根据 placement 计算并做边界修正
    popoverStyle() {
      if (this.isCenter) {
        const pw = 360
        const ph = 220
        return {
          top: Math.round((window.innerHeight - ph) / 2) + 'px',
          left: Math.round((window.innerWidth - pw) / 2) + 'px',
          width: pw + 'px',
        }
      }

      const { top, left, width, height } = this.targetRect
      const gap = 12
      const pw = 300
      const ph = 200
      const vw = window.innerWidth
      const vh = window.innerHeight
      const placement = (this.currentStep && this.currentStep.placement) || 'right'

      let x = left
      let y = top
      switch (placement) {
        case 'left': x = left - pw - gap; y = top; break
        case 'top': x = left; y = top - ph - gap; break
        case 'bottom': x = left; y = top + height + gap; break
        case 'right':
        default: x = left + width + gap; y = top; break
      }

      if (x < 8) x = 8
      if (x + pw > vw - 8) x = vw - pw - 8
      if (y < 8) y = 8
      if (y + ph > vh - 8) y = vh - ph - 8

      return { top: y + 'px', left: x + 'px', width: pw + 'px' }
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.stepIndex = 0
        this.$nextTick(this.locate)
      }
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
    // 用 document 级事件委托处理级联节点文字点击（checkStrictly 下只有点圆点会触发选中）
    // 必须用「捕获阶段」：级联面板(popperElm)上有 stopPropagation，冒泡阶段会被拦截
    document.addEventListener('click', this.handleDocumentClick, true)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.handleDocumentClick, true)
  },

  methods: {
    // resize 时重新定位当前步骤（用包装方法，避免把事件对象误传为 silent 参数）
    handleResize() {
      this.locate()
    },
    // 递归清理级联树中空的 children 节点，避免 Cascader 显示无内容的展开箭头
    cleanEmptyChildren(nodes) {
      if (!nodes || !nodes.length) return
      nodes.forEach((node) => {
        if (node.children && node.children.length) {
          this.cleanEmptyChildren(node.children)
        } else if (node.children) {
          delete node.children
        }
      })
    },
    // 递归查找目标 value 在级联树中的完整路径（返回 value 数组）
    findPath(nodes, target, path = []) {
      if (!nodes || !nodes.length) return null
      for (const node of nodes) {
        const nextPath = path.concat(node.value)
        if (node.value === target) return nextPath
        if (node.children && node.children.length) {
          const found = this.findPath(node.children, target, nextPath)
          if (found) return found
        }
      }
      return null
    },
    // 向上查找匹配指定选择器的祖先元素（兼容写法，含文本节点兜底）
    findClosest(el, selector) {
      let cur = el && el.nodeType === 1 ? el : (el ? el.parentElement : null)
      while (cur) {
        if (cur.matches && cur.matches(selector)) return cur
        cur = cur.parentElement
      }
      return null
    },
    // 点击级联节点文字时触发选中（checkStrictly 下默认只有点圆点才会选中）
    // 通过 document 级事件委托实现，比 @click.native 更可靠
    handleDocumentClick(e) {
      const labelEl = this.findClosest(e.target, '.el-cascader-node__label')
      if (!labelEl) return
      // 仅处理本引导组件的级联面板（通过 popper-class 区分）
      if (!this.findClosest(labelEl, '.guide-cascader-popper')) return
      const nodeEl = this.findClosest(labelEl, '.el-cascader-node')
      if (!nodeEl) return
      const radioInput = nodeEl.querySelector('.el-radio__original')
      if (!radioInput) return
      // 手动置为选中并派发 change 事件，确保 Vue 的 v-model 与 handleChange 都能响应
      radioInput.checked = true
      const changeEvent = document.createEvent('HTMLEvents')
      changeEvent.initEvent('change', true, false)
      radioInput.dispatchEvent(changeEvent)
    },
    // 级联目录选中某项后：关闭面板并跳转到对应引导步骤
    handleCascaderChange(values) {
      if (!values || !values.length) return
      const target = values[values.length - 1]
      // 选中后立即收起级联面板（checkStrictly 下不会自动关闭），避免面板停在原位
      this.closeCascader()
      if (typeof target === 'number' && target >= 0 && target !== this.stepIndex) {
        this.jumpToStep(target)
      }
    },
    // 收起级联下拉面板
    closeCascader() {
      this.$nextTick(() => {
        const cascader = this.$refs.cascader
        if (cascader && typeof cascader.toggleDropDownVisible === 'function') {
          cascader.toggleDropDownVisible(false)
        }
      })
    },
    /**
     * 跳转到指定步骤（用于级联目录直达）
     * 只执行目标在级联树「祖先路径」上的点击步骤（目标直接依赖的弹窗/标签页），
     * 不会顺序执行无关的前置点击，从而直接命中所选目标。
     * @param {Number} target 目标步骤下标
     */
    async jumpToStep(target) {
      // 目标在级联树里的完整路径（从根到目标）
      const path = this.findPath(this.cascaderOptions, target) || [target]
      // 只保留路径上的「点击」步骤（补齐直接依赖）+ 目标本身
      const runList = []
      path.forEach((idx) => {
        const s = this.steps[idx]
        if (idx === target || (s && s.action === 'click')) {
          runList.push(idx)
        }
      })

      for (const idx of runList) {
        this.stepIndex = idx
        this.$emit('change', idx)
        await this.locate(true) // silent：找不到元素时静默跳过，避免干扰跳转流程
      }
    },
    /**
     * 定位当前步骤：
     * 1. 根据步骤类型执行前置动作（折叠菜单 / 展开菜单 / 跳转路由）；
     * 2. 居中模式直接等路由切换后返回；
     * 3. 普通模式轮询等待目标元素可见后，滚动并记录其位置尺寸。
     * @param {Boolean} silent 静默模式（跳转补齐前置步骤时用）：找不到元素时不自动跳过
     */
    async locate(silent) {
      const step = this.currentStep
      if (!step) return

      // —— 前置动作（通过事件交给父组件处理） ——
      if (step.type === 'menu') {
        this.$emit('close-menus') // 折叠所有一级菜单，保证标题高亮准确
      } else if (step.type === 'submenu') {
        if (step.openMenuIndex != null) {
          this.$emit('open-menu', step.openMenuIndex) // 展开对应一级菜单
        }
      } else if (step.type === 'page' || step.type === 'element') {
        this.$emit('close-menus') // 进入页面介绍前折叠一级菜单，避免菜单一直展开
        if (step.route) {
          this.$emit('navigate', step.route) // 跳转到页面
        }
      }

      // —— 居中模式：无需定位元素，等待路由切换和页面渲染后即可 ——
      if (this.isCenter) {
        await this.sleep(step.wait || 600)
        return
      }

      // —— 普通模式：轮询等待目标元素出现且可见 ——
      // element 步骤（页面内元素）给更长等待，因为页面可能需要异步加载
      const interval = step.type === 'element' ? 500 : 300
      const el = await this.waitForElement(step.selector, interval, 15)
      if (!el) {
        // silent 模式（跳转补齐前置步骤）下静默返回，避免把 stepIndex 跳到别处
        if (!silent) this.skipOrNext()
        return
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      await this.sleep(300)
      const rect = el.getBoundingClientRect()
      this.targetRect = { top: rect.top, left: rect.left, width: rect.width, height: rect.height }

      // —— 自动点击动作（用于打开弹窗 / 切换标签页） ——
      // 当步骤配置 action === 'click' 时，定位后自动触发一次点击，
      // 例如点击"添加"按钮弹出弹窗、点击某个 tab 切换标签页。
      if (step.action === 'click') {
        el.click()
        await this.sleep(step.actionWait || 600)
      }
    },

    /**
     * 轮询查找元素，直到元素存在且可见（宽高 > 0）
     * 用于等待菜单展开动画、路由切换、异步组件加载完成。
     */
    waitForElement(selector, interval, maxTries) {
      return new Promise((resolve) => {
        let tries = 0
        const timer = setInterval(() => {
          tries++
          const el = document.querySelector(selector)
          if (el && el.getBoundingClientRect().width > 0) {
            clearInterval(timer)
            resolve(el)
          } else if (tries >= maxTries) {
            clearInterval(timer)
            resolve(null)
          }
        }, interval)
      })
    },

    // 简单延时
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    // 当前步骤找不到目标元素时：跳到下一步或直接完成
    skipOrNext() {
      if (!this.isLast) {
        this.next()
      } else {
        this.finish()
      }
    },

    // 下一步 / 完成
    next() {
      if (this.isLast) {
        this.finish()
        return
      }
      this.stepIndex++
      this.$emit('change', this.stepIndex)
      this.$nextTick(this.locate)
    },

    // 上一步
    prev() {
      if (this.stepIndex > 0) {
        this.stepIndex--
        this.$emit('change', this.stepIndex)
        this.$nextTick(this.locate)
      }
    },

    // 跳过：关闭引导
    skip() {
      this.$emit('update:visible', false)
      this.$emit('skip')
    },

    // 完成：关闭引导
    finish() {
      this.$emit('update:visible', false)
      this.$emit('finish')
    },
  },
}
</script>

<style lang="scss" scoped>
.guide-root {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  pointer-events: none; // 遮罩本身不拦截点击，交互由气泡按钮完成
}

// 半透明遮罩
.guide-mask {
  position: fixed;
  background: rgba(0, 0, 0, 0.6);
  transition: all 0.3s ease;
}

// 居中模式的全屏浅遮罩（能看到页面大致内容）
.guide-mask-full {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
}

// 高亮框
.guide-highlight {
  position: fixed;
  border: 2px solid #409eff;
  border-radius: 4px;
  box-shadow: 0 0 0 4px rgba(64, 158, 255, 0.25);
  z-index: 10000;
  transition: all 0.3s ease;
}

// 说明气泡
.guide-popover {
  position: fixed;
  z-index: 10001;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  padding: 16px;
  pointer-events: auto; // 气泡内部按钮需要可点击
  transition: all 0.3s ease;

  &.is-center {
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.28);
  }
}

// 气泡小箭头
.guide-popover-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);

  &.arrow-right { left: -6px; top: 24px; }
  &.arrow-left { right: -6px; top: 24px; }
  &.arrow-bottom { top: -6px; left: 24px; }
  &.arrow-top { bottom: -6px; left: 24px; }
}

// 级联目录导航（替代原纯文本标题）
.guide-cascader {
  width: 100%;
  margin-bottom: 8px;

  /deep/ .el-input {
    width: 100%;
  }

  // 下拉面板保持在气泡内部后，再显式抬高层级，避免被遮罩/高亮框盖住无法点击
  /deep/ .el-cascader__dropdown {
    z-index: 10002 !important;
  }

  // checkStrictly 模式下选中某节点后，让整条路径（一级/二级/三级）的圆点都显示为选中态，
  // 而不是只有最后选中的那个节点才有实心圆点
  /deep/ .el-cascader-node.in-checked-path .el-radio__inner {
    border-color: #409eff;
    background-color: #409eff;
  }
  /deep/ .el-cascader-node.in-checked-path .el-radio__inner::after {
    transform: translate(-50%, -50%) scale(1);
  }
}

.guide-popover-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.7;
  margin-bottom: 16px;
  white-space: pre-line;
}

.guide-popover-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.guide-popover-step {
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  color: #909399;
}
</style>



// WEBPACK FOOTER //
// src/components/guide/GuideTour.vue